import { useTranslate } from "../../locales/hooks";

type InterviewHeaderProps = {
  avatar?: React.ReactNode;
  usedMessages?: number;
  totalMessages?: number;
  usedTokens?: number;
  totalTokens?: number;
  onReset?: () => void;
  variant?: "normal" | "warning" | "hard-stop";
};

const InterviewHeader = ({
  avatar,
  usedMessages = 4,
  totalMessages = 20,
  onReset,
  variant = "normal",
  usedTokens = 0,
  totalTokens = 10000,
}: InterviewHeaderProps) => {
  const t = useTranslate();

  const getLimitBadgeColors = () => {
    if (variant === "hard-stop") {
      return "bg-[#fef2f2] border-[#fecaca] text-[#991b1b]";
    }
    if (variant === "warning") {
      return "bg-[#fffbeb] border-[#fde68a] text-[#92400e]";
    }
    return "bg-[#f8fafc] border-[#ddd] text-[#334155]";
  };

  const formatTokens = (tokens: number): string => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  const tokenPercentage = usedTokens === 0 ? 0 : Math.min((usedTokens / totalTokens) * 100, 100);

  return (
    <div
      className="relative shrink-0 w-full border-b border-[#ddd]"
      data-name="interview-header"
    >
      <div className="flex items-center gap-[14px] px-[20px] py-[14px] w-full">
        {avatar && <div className="shrink-0">{avatar}</div>}

        {/* Token usage indicator */}
        <div className="flex-1 min-w-0 flex flex-col gap-[6px]">
          <div className="flex items-center justify-between gap-[10px]">
            <p className="font-['Manrope:Regular',sans-serif] font-normal text-[#717182] text-[11px] truncate">
              {t("interviewHeader__tokensLabel")}
            </p>
            <p className="font-['Manrope:Medium',sans-serif] font-medium text-[#334155] text-[11px] whitespace-nowrap">
              {formatTokens(usedTokens)} / {formatTokens(totalTokens)}
            </p>
          </div>
          <div className="w-full h-[4px] bg-[#e9ebef] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                tokenPercentage >= 90
                  ? 'bg-[#dc2626]'
                  : tokenPercentage >= 70
                  ? 'bg-[#d97706]'
                  : 'bg-[#0f172a]'
              }`}
              style={{ width: `${tokenPercentage}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-[10px] shrink-0">
          <div
            className={`flex items-center gap-[5px] px-[10px] py-[5px] rounded-[8px] border ${getLimitBadgeColors()}`}
          >
            <p className="font-['Manrope:Medium',sans-serif] font-medium text-[12px] whitespace-nowrap">
              {usedMessages} из {totalMessages}
            </p>
          </div>
          {onReset && (
            <button
              onClick={onReset}
              className="cursor-pointer flex items-center px-[12px] py-[6px] rounded-[8px] border border-[#ddd] hover:bg-[#f8fafc] transition-colors"
            >
              <p className="font-['Manrope:Regular',sans-serif] font-normal text-[#334155] text-[12px] whitespace-nowrap">
                {t("interviewHeader__resetButton")}
              </p>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewHeader;
